/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { getRichTextHtml } from '@msdyn365-commerce/core';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import TourIFrame from '../../components/truetour-iframe';
import VimeoIFrame from '../../components/vimeo-iframe';
import YoutubeIFrame from '../../components/youtube-iframe';
import { IYoutubeModalData } from './youtube-modal.data';
import { IYoutubeModalProps } from './youtube-modal.props.autogenerated';

// ==============================================================================
// CLASS DEFINITIONS
// ==============================================================================
/**
 *
 * YoutubeModal component
 * @extends {React.Component<IYoutubeModalProps<IYoutubeModalData>>}
 */
@observer
class YoutubeModal extends React.Component<IYoutubeModalProps<IYoutubeModalData>> {

    // ==========================================================================
    // FIELDS
    // ==========================================================================

    @observable private _modalState: boolean = false;

    // ==========================================================================
    // PUBLIC METHODS
    // ==========================================================================

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    public constructor(props: IYoutubeModalProps<IYoutubeModalData>) {
        super(props);

        this._openModal = this._openModal.bind(this);
        this._closeModal = this._closeModal.bind(this);
    }

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    public render(): JSX.Element {
        return (
            <div className={this.props.config.cssEntry} >
                <div
                    onClick={this._openModal}
                    onKeyPress={this._openModal}
                    role='button'
                    aria-label='Video Play Button, click here to play the video'
                    className='youtube-modal-clickable'
                    tabIndex={0}
                >
                    {this._richText}
                    {this._thumbnail}
                </div>
                {this._modal}
            </div>
        );
    }

    // ==========================================================================
    // JSX CONSTRUCTORS
    // ==========================================================================

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    private get _thumbnail(): JSX.Element | null {
        if (this.props.config.image) {
            const {
                altText,
                src
            } = this.props.config.image;

            let imgSrc = src || '';
            if (!imgSrc.includes('&q=')) {
                imgSrc += '&q=80';
            }

            return (
                <img
                    src={imgSrc}
                    alt={altText}
                />
            );
        }

        // No thumbnail supplied
        return null;
    }

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    private get _richText(): JSX.Element | null {
        if (this.props.config.richText) {
            return (
                // tslint:disable-next-line:react-no-dangerous-html
                <div
                    className='youtube-modal-textblock'
                    dangerouslySetInnerHTML={
                        getRichTextHtml(this.props.config.richText)
                    }
                />
            );
        }

        // No text area was supplied
        return null;
    }

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    private get _modal(): JSX.Element | null {

        let modalId;
        if (this.props.config.videoId) {
            modalId = `youtube-${this.props.config.videoId}`;
        }
        if (this.props.config.vimeoVideoId) {
            modalId = `vimeo-${this.props.config.vimeoVideoId}`;
        }
        if(this.props.config.trueTourId) {
            modalId = `truetour-${this.props.config.trueTourId}`;
        }

        if (modalId) {
            return (
                <Modal
                    isOpen={this._modalState}
                    toggle={this._closeModal}
                    modalClassName='youtube-modal'
                    aria-labelledby={modalId}
                >
                    <ModalHeader toggle={this._closeModal} tabIndex={0} />
                    <ModalBody>
                        {
                            this._modalState &&
                            this._videoType
                        }
                    </ModalBody>
                </Modal>
            );
        }

        console.warn('[Youtube-Modal] video id is not defined');
        return null;
    }

    // ==========================================================================
    // COMPONENT EVENTS
    // ==========================================================================

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    private _openModal(): void {
        this._modalState = true;
    }

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    private _closeModal(): void {
        this._modalState = false;
    }

    // -----------------------------------------------------------
    // -----------------------------------------------------------
    private get _videoType(): JSX.Element | null {
        // uses youtube ID first regardless of vimeo ID
        if (this.props.config.videoId) {
            return <YoutubeIFrame youtubeId={this.props.config.videoId} />;
        }
        if (this.props.config.vimeoVideoId) {
            return <VimeoIFrame vimeoId={this.props.config.vimeoVideoId} />;
        }
        if (this.props.config.trueTourId) {
            return <TourIFrame trueTourId={this.props.config.trueTourId} />;
        }
        return null;
   }
}

export default YoutubeModal;